import { RBAC_PERMISSION } from 'src/app/rbac/rbac.codes';
export interface AuditReasons {
  common: string;
  fields?: Record<string, string>;
}

export type AuditReason = string | AuditReasons;

export interface AuditFieldChange {
  key: string;
  label: string;
  oldValue: string;
  newValue: string;
  oldImage?: string;
  newImage?: string;
}

/**
 * Only for what the form cannot tell on its own: `key` is the backend's audit column when it differs from
 * the form control (`control`), which per-field reasons are keyed by. Label and value names are dynamic:
 * the label comes from the field's `<label>` on the open form, an id or code from its dropdown's options.
 */
export interface AuditFieldDef {
  key: string;
  label?: string;
  control?: string;
  display?: (value: unknown) => string;
  blank?: () => string;
}

const EMPTY = '—';
const UNKNOWN = 'Unknown';
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})(T.*)?$/;
/** Record keys, not data: never listed in a reason popup. */
const NEVER_AUDITED = ['id'];
/** The element a form field renders as: native controls carry `formcontrolname`, `app-multi-select` its `data-control`. */
const fieldSelector = (control: string) => `[formcontrolname="${control}"], [data-control="${control}"]`;

/**
 * Every top-level form value that changed, like CTMS's `changedFields`: nothing is registered per form.
 * Each change is labelled with the field's own `<label>` on the open form, and an id or code reads as the
 * name its dropdown showed. `fields` are only for what the form cannot tell (backend column names, the
 * study logo) and are listed first; every other changed value follows in form order. `exclude` names
 * controls that are not data (helper flags, fixed values).
 */
export function buildAuditChanges(originalSnapshot: string, currentValue: unknown, fields: AuditFieldDef[] = [],
                                  exclude: string[] = []): AuditFieldChange[] {
  const original = parse(originalSnapshot);
  const current = normalize(currentValue) as Record<string, unknown>;
  const covered = new Set(fields.flatMap(field => [field.key, field.control ?? field.key]));
  const skipped = new Set([...NEVER_AUDITED, ...exclude]);
  const unlisted: AuditFieldDef[] = [...new Set([...Object.keys(current ?? {}), ...Object.keys(original ?? {})])]
    .filter(key => !covered.has(key) && !skipped.has(key))
    .map(key => ({ key }));
  const form = openFormRoot();
  return [...fields, ...unlisted]
    .map(field => {
      const property = field.control ?? field.key;
      const before = original?.[property];
      const after = current?.[property];
      if (sameValue(before, after)) {
        return null;
      }
      const label = field.label ?? formLabel(form, property) ?? fieldLabel(field.key);
      return { key: field.key, label, oldValue: display(before, field, property), newValue: display(after, field, property) };
    })
    .filter((change): change is AuditFieldChange => change !== null);
}

/** The topmost open modal (the form being saved; the reason popup is not open yet), else the page. */
function openFormRoot(): ParentNode | null {
  if (typeof document === 'undefined') {
    return null;
  }
  const modals = document.querySelectorAll('ngb-modal-window');
  return modals.length ? modals[modals.length - 1] : document;
}

/**
 * The text the form shows for a control: its `<label>` (`label[for]` when the field has an id, otherwise the
 * nearest enclosing block that holds this field alone and a label outside it; a dropdown's own inner labels,
 * such as Select All, are skipped), else its placeholder ("Select Phase" → "Phase") for a field without one.
 */
function formLabel(root: ParentNode | null, control: string): string | null {
  const field = root?.querySelector<HTMLElement>(fieldSelector(control));
  if (!field) {
    return null;
  }
  const byFor = field.id ? root?.querySelector<HTMLLabelElement>(`label[for="${CSS.escape(field.id)}"]`) : null;
  return (byFor ? labelText(byFor) : enclosingLabel(field)) ?? placeholderText(field);
}

function enclosingLabel(field: HTMLElement): string | null {
  let block = field.parentElement;
  for (let depth = 0; block && depth < 4; depth++, block = block.parentElement) {
    const otherFields = Array.from(block.querySelectorAll('[formcontrolname], [data-control]'))
      .some(other => other !== field && !field.contains(other));
    if (otherFields) {
      return null;
    }
    const label = Array.from(block.querySelectorAll('label')).find(candidate => !field.contains(candidate));
    if (label) {
      return labelText(label);
    }
  }
  return null;
}

function placeholderText(field: HTMLElement): string | null {
  const text = field.getAttribute('placeholder')
    ?? field.querySelector('[placeholder]')?.getAttribute('placeholder')
    ?? field.querySelector('.ng-placeholder')?.textContent
    ?? '';
  const words = text.replace(/^\s*select\s+/i, '').replace(/\s+/g, ' ').trim();
  return words || null;
}

function labelText(label: Element): string | null {
  const text = (label.textContent ?? '').replace(/\*/g, '').replace(/\s+/g, ' ').replace(/:\s*$/, '').trim();
  return text || null;
}

/**
 * Option names per form control, published by the dropdowns (`app-multi-select`, `ng-select` via
 * `RememberOptionsDirective`) as they render, so a changed id or code reads as the name the user picked.
 * Kept per control: one code can name different things in different lists (country `IN` vs a state code).
 */
const optionLabels = new Map<string, Map<string, string>>();

export function rememberOptions(control: string, options: readonly unknown[] | null | undefined,
                                valueKey: string, labelKey: string): void {
  if (!control || !Array.isArray(options)) {
    return;
  }
  const labels = optionLabels.get(control) ?? new Map<string, string>();
  for (const option of options) {
    if (option === null || typeof option !== 'object') {
      continue;
    }
    const value = (option as Record<string, unknown>)[valueKey];
    const label = (option as Record<string, unknown>)[labelKey];
    if (value !== null && value !== undefined && typeof label === 'string' && label.trim()) {
      labels.set(String(value), label.trim());
    }
  }
  optionLabels.set(control, labels);
}

function rememberedLabel(control: string, value: unknown): string | undefined {
  return optionLabels.get(control)?.get(String(value));
}

/** `plannedSubject` → "Planned subject", `isActive` → "Active", `phaseId` → "Phase": sentence case, like the hand-written labels. */
export function fieldLabel(key: string): string {
  const words = key
    .replace(/^is(?=[A-Z])/, '')
    .replace(/Ids?$/, '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : key;
}

export function encodeAuditReasons(reason: AuditReason): string {
  const payload = typeof reason === 'string'
    ? { v: 1, common: reason.trim() }
    : { v: 1, common: reason.common?.trim() ?? '', fields: trimmedFields(reason.fields) };
  const bytes = new TextEncoder().encode(JSON.stringify(payload));
  let binary = '';
  bytes.forEach(byte => (binary += String.fromCharCode(byte)));
  return btoa(binary);
}

function trimmedFields(fields: Record<string, string> | undefined): Record<string, string> {
  const result: Record<string, string> = {};
  Object.entries(fields ?? {}).forEach(([key, value]) => {
    if (value?.trim()) {
      result[key] = value.trim();
    }
  });
  return result;
}

function parse(snapshot: string): Record<string, unknown> | null {
  try {
    return JSON.parse(snapshot) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function normalize(value: unknown): unknown {
  return value === undefined ? undefined : JSON.parse(JSON.stringify(trim(value)));
}

function trim(value: unknown): unknown {
  if (typeof value === 'string') {
    return value.trim();
  }
  if (Array.isArray(value)) {
    return value.map(trim);
  }
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value as Record<string, unknown>).map(([k, v]) => [k, trim(v)]));
  }
  return value;
}

function isBlank(value: unknown): boolean {
  return value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0);
}

function sameValue(before: unknown, after: unknown): boolean {
  if (isBlank(before) && isBlank(after)) {
    return true;
  }
  if (Array.isArray(before) && Array.isArray(after)) {
    return JSON.stringify(before.map(item => JSON.stringify(item)).sort()) === JSON.stringify(after.map(item => JSON.stringify(item)).sort());
  }
  return JSON.stringify(before) === JSON.stringify(after);
}

function display(value: unknown, field: AuditFieldDef, control: string): string {
  if (isBlank(value)) {
    return field.blank?.() || EMPTY;
  }
  if (Array.isArray(value)) {
    return value.map(item => display(item, field, control)).sort().join(', ');
  }
  const text = field.display ? field.display(value) : rememberedLabel(control, value) ?? plainText(value);
  if (!text) {
    return EMPTY;
  }
  return UUID_PATTERN.test(text) ? UNKNOWN : text;
}

function plainText(value: unknown): string {
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  const date = asDate(value);
  if (date) {
    return formatDate(date);
  }
  if (typeof value === 'object' && value !== null) {
    const named = value as { name?: unknown; label?: unknown };
    return named.name !== undefined || named.label !== undefined ? String(named.name ?? named.label) : '';
  }
  return String(value);
}

/** Datepicker values ({year, month, day}), ISO strings and Date objects. */
function asDate(value: unknown): Date | null {
  if (value instanceof Date) {
    return value;
  }
  if (typeof value === 'object' && value !== null) {
    const { year, month, day } = value as { year?: unknown; month?: unknown; day?: unknown };
    if (typeof year === 'number' && typeof month === 'number' && typeof day === 'number') {
      return new Date(year, month - 1, day);
    }
    return null;
  }
  if (typeof value === 'string') {
    const match = ISO_DATE_PATTERN.exec(value);
    if (match) {
      return match[4] ? new Date(value) : new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    }
  }
  return null;
}

function formatDate(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
}

const PERMISSION_ORDER: string[] = [RBAC_PERMISSION.READ, RBAC_PERMISSION.WRITE, RBAC_PERMISSION.DELETE];

export function formatPermissions(permissions: Iterable<string> | null | undefined): string {
  const codes = Array.from(permissions ?? []);
  if (codes.length === 0) {
    return EMPTY;
  }
  const rank = (code: string) => (PERMISSION_ORDER.indexOf(code) + 1) || PERMISSION_ORDER.length + 1;
  return [...new Set(codes)].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b)).join(',');
}

export function gridRowChange(subIdentifierId: string, parameter: string, label: string,
                              oldValue: string, newValue: string): AuditFieldChange | null {
  return oldValue === newValue ? null : { key: `${subIdentifierId}:${parameter}`, label, oldValue, newValue };
}
